import { useCallback, useEffect, useMemo } from 'react';

import { Drawer } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useChangeLocation } from '../../hooks/useChangeLocation';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import useDrawer from '../../hooks/useDrawer';
import { MapFormIncident } from '../../modules/Map/feature/IncidentMap';
import { MapFormLocation } from '../../modules/Map/feature/LocationMap';
import { ProjectTasksColumnsSettingsDrawer, DailyTasksColumnsSettingsDrawer } from '../../modules/Tasks';
import { ProjectTaskForm } from '../../modules/Tasks/components/ProjectTaskForm/ProjectTaskForm';
import type { DrawerType } from '../../state/drawer';
import { Drawer as DrawerEnum } from '../../state/drawer';
import { DrawerNavigation } from '../DrawerNavigation';
import { LocationsManagerModal } from '../EventEditForm/components/LocationsManagerModal';
import { FilterForm } from '../FilterForm';
import { FilterFormDailyTasks } from '../FilterFormDailyTasks';
import { FilterFormProjectTasks } from '../FilterFormProjectTasks';
import { useFullScreen } from '../FullScreen';
import { AddIncidentForm } from '../IncidentForm';
import { EditIncidentTableForm } from '../IncidentForm/EditIncidentForms';
import { IncidentUpdatesForm } from '../IncidentUpdatesForm';
import { PhotosAddForm, PhotosEditForm } from '../PhotosEditorForm';
import { TableColumnForm } from '../TableColumnForm';
import { UserCreateOrAddForm } from '../UserCreateOrAddForm/UserCreateOrAddForm';
import { UserEditForm } from '../UserEditForm';
import { UserFormSettings } from '../UserFormSettings';

const getDrawerForm = (drawer: DrawerType, closeDrawer: () => void, setModalChange: (value: boolean) => void) => {
  switch (drawer.type) {
    case DrawerEnum.INCIDENT_PHOTO_EDIT: {
      return <PhotosEditForm {...drawer.props} onEndAction={closeDrawer} />;
    }
    case DrawerEnum.INCIDENT_PHOTO_ADD: {
      return <PhotosAddForm {...drawer.props} onEndAction={closeDrawer} />;
    }
    case DrawerEnum.INCIDENT_ADD: {
      return <AddIncidentForm onEndAction={closeDrawer} setModalChange={setModalChange} />;
    }
    case DrawerEnum.INCIDENT_EDIT: {
      return (
        <DrawerNavigation>
          <EditIncidentTableForm {...drawer.props} onEndAction={closeDrawer} setModalChange={setModalChange} />
        </DrawerNavigation>
      );
    }
    case DrawerEnum.INCIDENT_TABLE_COLUMNS: {
      return <TableColumnForm />;
    }
    case DrawerEnum.MAP: {
      return (
        <DrawerNavigation>
          <MapFormIncident {...drawer.props} onEndAction={closeDrawer} setModalChange={setModalChange} />
        </DrawerNavigation>
      );
    }
    case DrawerEnum.INCIDENT_FILTER: {
      return <FilterForm />;
    }
    case DrawerEnum.EVENT_LOCATION: {
      return (
        <DrawerNavigation>
          <MapFormLocation {...drawer.props} onEndAction={closeDrawer} />
        </DrawerNavigation>
      );
    }
    case DrawerEnum.USER_CREATE: {
      return <UserCreateOrAddForm />;
    }
    case DrawerEnum.USER_EDIT: {
      return <UserEditForm {...drawer.props} />;
    }
    case DrawerEnum.INCIDENTS_UPDATES: {
      return (
        <DrawerNavigation>
          <IncidentUpdatesForm onEndAction={closeDrawer} />
        </DrawerNavigation>
      );
    }
    case DrawerEnum.PROJECT_TASKS_COLUMNS: {
      return <ProjectTasksColumnsSettingsDrawer />;
    }
    case DrawerEnum.PROJECT_TASKS_FILTER: {
      return <FilterFormProjectTasks />;
    }
    case DrawerEnum.DAILY_TASKS_COLUMNS: {
      return <DailyTasksColumnsSettingsDrawer />;
    }
    case DrawerEnum.DAILY_TASKS_FILTER: {
      return <FilterFormDailyTasks />;
    }
    case DrawerEnum.USES_FORM_SETTINGS: {
      return <UserFormSettings {...drawer.props} onEndAction={closeDrawer} />;
    }
    case DrawerEnum.PROJECT_TASK_FORM: {
      return <ProjectTaskForm {...drawer.props} onEndAction={closeDrawer} setModalChange={setModalChange} />;
    }
    case DrawerEnum.LOCATION_LIST_MANAGER: {
      return (
        <DrawerNavigation>
          <LocationsManagerModal onEndAction={closeDrawer} />
        </DrawerNavigation>
      );
    }
    default: {
      return null;
    }
  }
};

export const DrawerStack = () => {
  const { drawer, isOpen, closeDrawer, modalChange } = useDrawer();
  const { t } = useTranslation();
  const { showConfirm, confirmModal } = useConfirmDialog({
    title: t('notifications.exit.title'),
    content: t('notifications.exit.content'),
  });
  const { ref } = useFullScreen();

  const handleClose = useCallback(async () => {
    if (modalChange.current) {
      await showConfirm().then((isOk) => {
        if (isOk) {
          closeDrawer();
          modalChange.current = false;
        }
      });
    } else {
      closeDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalChange, drawer?.type]);

  const setModalChange = useCallback(
    (value: boolean) => {
      modalChange.current = value;
    },
    [modalChange],
  );

  const form = useMemo(
    () => (drawer ? getDrawerForm(drawer, handleClose, setModalChange) : null),
    [drawer, handleClose, setModalChange],
  );

  useChangeLocation(handleClose);

  useEffect(() => {
    if (isOpen) {
      modalChange.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return isOpen ? (
    <Drawer container={ref.current} open={isOpen} onClose={handleClose} anchor='right'>
      {form}
      {confirmModal}
    </Drawer>
  ) : null;
};
